import { useState } from 'react';
import { useInView } from '@/hooks/useInView';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle, Mail, Phone, MapPin, ArrowRight } from 'lucide-react';
import { sendNotification } from '@/services/mushinNotifier';
import { buildContactEmailHtml, getNotificationRecipient } from '@/services/formEmailHtml';

const RED = '#E02D45';

const exploringOptions = [
  'Plant Quality & Compliance', 'Supplier Quality Management', 'End-to-End Traceability',
  'Machine Data & IoT', 'Multi-plant Rollout', 'Full Digital Transformation', 'Other',
];

function Section({ children, className = '' }) {
  const [ref, inView] = useInView();
  return <section ref={ref} className={`${inView ? 'animate-fade-in-up' : 'opacity-0'} ${className}`}>{children}</section>;
}

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    name: '', company: '', role: '', email: '', phone: '', plants: '', exploring: '', message: '',
  });

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const to = getNotificationRecipient();
      const subject = `New Contact Inquiry from ${form.name} - ${form.company}`;
      await sendNotification({
        channel: 'email',
        to,
        subject,
        html: buildContactEmailHtml(form),
      });
      setSuccess(true);
      setForm({ name: '', company: '', role: '', email: '', phone: '', plants: '', exploring: '', message: '' });
    } catch (err) {
      console.error('Contact submission failed:', err);
    }
    setLoading(false);
  };

  return (
    <div className="bg-[#060606]">
      <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(${RED}30 1px, transparent 1px), linear-gradient(90deg, ${RED}30 1px, transparent 1px)`,
          backgroundSize: '80px 80px'
        }} />

        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20">

            {/* LEFT — Info */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: RED }} />
                <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: RED }}>Contact Mushin</p>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[4rem] font-black uppercase tracking-[-0.03em] text-white leading-[0.92]" data-testid="contact-title">
                Let's Talk About<br />Your Plant
              </h1>
              <p className="mt-8 text-base text-white/40 leading-[1.7] font-medium max-w-md">
                Whether you are exploring Mushin for the first time or planning an enterprise rollout, our team is ready to help.
              </p>

              <div className="mt-10 space-y-5">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center border flex-shrink-0" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                    <Phone className="w-4 h-4" style={{ color: RED }} strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/20">Phone</p>
                    <a href="tel:+919599907773" className="text-sm font-bold text-white/70 hover:text-white transition-colors">+91 95999 07773</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center border flex-shrink-0" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                    <Mail className="w-4 h-4" style={{ color: RED }} strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/20">Sales & Quotes</p>
                    <a href="mailto:sales@mushinlabs.com" className="text-sm font-bold text-white/70 hover:text-white transition-colors">sales@mushinlabs.com</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center border flex-shrink-0" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                    <Mail className="w-4 h-4" style={{ color: RED }} strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/20">General Inquiries</p>
                    <a href="mailto:hello@mushinlabs.com" className="text-sm font-bold text-white/70 hover:text-white transition-colors">hello@mushinlabs.com</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center border flex-shrink-0" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                    <MapPin className="w-4 h-4" style={{ color: RED }} strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/20">Office</p>
                    <p className="text-sm font-bold text-white/70">Sector 28, DLF Phase 1, Gurugram</p>
                  </div>
                </div>
              </div>

              <div className="mt-10 bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6">
                <h3 className="text-sm font-bold text-white/80 uppercase">Enterprise Rollout Inquiry</h3>
                <p className="mt-2 text-[13px] text-white/35 leading-[1.7] font-medium">
                  For multi-plant deployments, enterprise licensing, or custom implementation discussions, reach out to our team directly.
                </p>
                <a href="mailto:sales@mushinlabs.com" className="inline-flex items-center gap-2 mt-4 text-sm font-bold transition-colors hover:text-white" style={{ color: RED }} data-testid="enterprise-inquiry-cta">
                  Contact Enterprise Sales <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>

            {/* RIGHT — Form */}
            <div>
              {success ? (
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-12 text-center" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 border" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                    <CheckCircle className="w-8 h-8" style={{ color: RED }} />
                  </div>
                  <h2 className="text-2xl font-black uppercase text-white">Thank You</h2>
                  <p className="mt-3 text-white/40 font-medium">Our team will review your inquiry and respond within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-8" data-testid="contact-form">
                  <h2 className="text-lg font-bold text-white uppercase tracking-tight mb-6">Send Us a Message</h2>
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white/30 text-[10px] font-bold uppercase tracking-[0.15em]">Name *</Label>
                        <Input required value={form.name} onChange={update('name')} placeholder="Full name"
                          className="bg-white/[0.04] border-white/[0.08] text-white mt-1.5 focus-visible:ring-[#E02D45] placeholder:text-white/20" data-testid="contact-name" />
                      </div>
                      <div>
                        <Label className="text-white/30 text-[10px] font-bold uppercase tracking-[0.15em]">Company *</Label>
                        <Input required value={form.company} onChange={update('company')} placeholder="Company name"
                          className="bg-white/[0.04] border-white/[0.08] text-white mt-1.5 focus-visible:ring-[#E02D45] placeholder:text-white/20" data-testid="contact-company" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white/30 text-[10px] font-bold uppercase tracking-[0.15em]">Role</Label>
                        <Input value={form.role} onChange={update('role')} placeholder="Your role"
                          className="bg-white/[0.04] border-white/[0.08] text-white mt-1.5 focus-visible:ring-[#E02D45] placeholder:text-white/20" data-testid="contact-role" />
                      </div>
                      <div>
                        <Label className="text-white/30 text-[10px] font-bold uppercase tracking-[0.15em]">Email *</Label>
                        <Input required type="email" value={form.email} onChange={update('email')} placeholder="work@company.com"
                          className="bg-white/[0.04] border-white/[0.08] text-white mt-1.5 focus-visible:ring-[#E02D45] placeholder:text-white/20" data-testid="contact-email" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white/30 text-[10px] font-bold uppercase tracking-[0.15em]">Phone</Label>
                        <Input value={form.phone} onChange={update('phone')} placeholder="+91 98xxx xxxxx"
                          className="bg-white/[0.04] border-white/[0.08] text-white mt-1.5 focus-visible:ring-[#E02D45] placeholder:text-white/20" data-testid="contact-phone" />
                      </div>
                      <div>
                        <Label className="text-white/30 text-[10px] font-bold uppercase tracking-[0.15em]">Number of Plants</Label>
                        <Input value={form.plants} onChange={update('plants')} placeholder="e.g. 3"
                          className="bg-white/[0.04] border-white/[0.08] text-white mt-1.5 focus-visible:ring-[#E02D45] placeholder:text-white/20" data-testid="contact-plants" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-white/30 text-[10px] font-bold uppercase tracking-[0.15em]">What Are You Exploring?</Label>
                      <div className="flex flex-wrap gap-2 mt-2.5">
                        {exploringOptions.map((opt) => (
                          <button key={opt} type="button"
                            onClick={() => setForm({ ...form, exploring: opt })}
                            className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-all duration-200 ${form.exploring === opt ? 'text-white border' : 'text-white/35 border border-white/[0.08] hover:border-white/15 hover:text-white/50'}`}
                            style={form.exploring === opt ? { backgroundColor: `${RED}15`, borderColor: `${RED}40`, color: RED } : {}}
                            data-testid={`exploring-${opt.toLowerCase().replace(/\s+/g, '-')}`}>
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-white/30 text-[10px] font-bold uppercase tracking-[0.15em]">Message</Label>
                      <Textarea value={form.message} onChange={update('message')} placeholder="Tell us about your requirements..."
                        className="bg-white/[0.04] border-white/[0.08] text-white mt-1.5 focus-visible:ring-[#E02D45] placeholder:text-white/20 min-h-[100px]" data-testid="contact-message" />
                    </div>
                    <button type="submit" disabled={loading}
                      className="w-full text-white py-3.5 rounded-full font-bold text-sm transition-all duration-300 disabled:opacity-50 hover:shadow-lg"
                      style={{ backgroundColor: RED }}
                      data-testid="contact-submit">
                      {loading ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
